import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePdfThumbnailGenerator } from './usePdfThumbnailGenerator';

interface DocumentToProcess {
  id: string;
  file_path: string;
  user_id: string;
}

// Singleton queue to avoid duplicates across components/re-renders
const thumbnailQueue = new Map<string, DocumentToProcess>();
let isProcessing = false;
let processedCount = 0;
let totalQueued = 0;

// Store the generator function reference
let generatorFn: ((file: File, documentId: string, userId: string) => Promise<{ success: boolean; error?: string }>) | null = null;

const processNext = async () => {
  if (isProcessing || thumbnailQueue.size === 0 || !generatorFn) return;
  
  isProcessing = true;
  
  // Get first item from queue
  const iterator = thumbnailQueue.entries().next();
  if (iterator.done) {
    isProcessing = false;
    return;
  }
  
  const [docId, doc] = iterator.value;
  thumbnailQueue.delete(docId);
  
  try {
    // Download PDF from Storage
    const { data, error } = await supabase.storage
      .from('documents')
      .download(doc.file_path);
    
    // SECURITY: If file not found (deleted from storage but still in DB), skip silently
    if (error || !data) {
      console.warn('[ThumbnailQueue] File not found, skipping:', docId);
      isProcessing = false;
      processedCount++;
      console.log(`[ThumbnailQueue] Progress: ${processedCount}/${totalQueued}`);
      
      // Small delay before next to let browser breathe
      setTimeout(() => processNext(), 200);
      return;
    }
    
    // Create File object from Blob
    const file = new File([data], 'document.pdf', { type: 'application/pdf' });
    
    // Generate and upload thumbnail
    const result = await generatorFn(file, doc.id, doc.user_id);
    
    processedCount++;
    
    if (result.success) {
      console.log(`[ThumbnailQueue] ✓ Generated: ${docId} (${processedCount}/${totalQueued})`);
    } else {
      console.warn(`[ThumbnailQueue] Failed: ${docId} - ${result.error}`);
    }
    
    // 500ms delay between each PDF for browser stability
    await new Promise(resolve => setTimeout(resolve, 500));
    
  } catch (error) {
    console.warn('[ThumbnailQueue] Silent failure for:', docId, error);
    processedCount++;
  }
  
  isProcessing = false;
  processNext(); // Process next in queue
};

export function useThumbnailQueue() {
  const { generatePdfThumbnail } = usePdfThumbnailGenerator();
  
  // Store generator reference for singleton access
  generatorFn = generatePdfThumbnail;

  const addToQueue = useCallback((doc: DocumentToProcess) => {
    // Skip if already in queue
    if (thumbnailQueue.has(doc.id)) return;
    
    thumbnailQueue.set(doc.id, doc);
    totalQueued++;
    
    // Start processing if not already running
    processNext();
  }, []);

  const getQueueStats = useCallback(() => ({
    queueSize: thumbnailQueue.size,
    processed: processedCount,
    total: totalQueued,
    isProcessing
  }), []);

  return { 
    addToQueue,
    getQueueStats
  };
}
